import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  minBetStakeType,
  predefinedStakesType
} from 'utils/constants/betslip/bet-types';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { thousandSeparator } from 'utils/helpers/validators';
import { roundAmount } from 'newelements/_Betslip/utils/roundings/round-amount';
import { useCurrency } from 'hooks/useCurrency';
import { setRSPredefinedStakes } from 'store/actions/betslip';
import { getPredefinedStakes } from 'store/selectors/betslip';
import { getPartnerConfigs } from 'store/selectors/socket';

type PredefinedStakes = {
  [key: string]: string | number | undefined;
};

type TUsePredefinedStakes = {
  predefinedStakes: {
    default_stake: any;
    stake_1: any;
    stake_2: any;
    stake_3: any;
  };
  setPredefinedStakes: (values: PredefinedStakes) => void;
};

export const usePredefinedStakes = (): TUsePredefinedStakes => {
  const dispatch = useDispatch();
  const partnerConfigs = useSelector(getPartnerConfigs);
  const { currencyId } = useCurrency();

  const storePredefinedStakes = useSelector(getPredefinedStakes);

  const defaultStakes = useMemo(() => {
    let predefinedStakes: PredefinedStakes = {};

    if (SpringConfigs.PREDEFINE_STAKES_ENABLE) {
      predefinedStakes = {
        stake_1: +roundAmount(SpringConfigs.PREDEFINE_STAKE_1),
        stake_2: +roundAmount(SpringConfigs.PREDEFINE_STAKE_2),
        stake_3: +roundAmount(SpringConfigs.PREDEFINE_STAKE_3)
      };
    }

    if (SpringConfigs.DEFAULT_STAKE_ENABLED) {
      predefinedStakes.default_stake = +roundAmount(
        SpringConfigs.DEFAULT_STAKE
      );
    }

    if (partnerConfigs && currencyId) {
      const minBetStake = partnerConfigs.min_bet_stakes[currencyId];

      return {
        stake_1: roundAmount(
          predefinedStakes.stake_1 ||
            minBetStake * minBetStakeType.MULTIPLIER_10 ||
            predefinedStakesType.STAKE_TYPE_1
        ),
        stake_2: roundAmount(
          predefinedStakes.stake_2 ||
            minBetStake * minBetStakeType.MULTIPLIER_20 ||
            predefinedStakesType.STAKE_TYPE_2
        ),
        stake_3: roundAmount(
          predefinedStakes.stake_3 ||
            minBetStake * minBetStakeType.MULTIPLIER_50 ||
            predefinedStakesType.STAKE_TYPE_3
        ),
        default_stake: predefinedStakes.default_stake || ''
      };
    } else {
      return {
        default_stake: '',
        stake_1: roundAmount(
          predefinedStakes.stake_1 || predefinedStakesType.STAKE_TYPE_1
        ),
        stake_2: roundAmount(
          predefinedStakes.stake_2 || predefinedStakesType.STAKE_TYPE_2
        ),
        stake_3: roundAmount(
          predefinedStakes.stake_3 || predefinedStakesType.STAKE_TYPE_3
        )
      };
    }
  }, [partnerConfigs, currencyId]);

  const predefinedStakes = useMemo(() => {
    const { default_stake, stake_1, stake_2, stake_3 } =
      storePredefinedStakes || {};

    return {
      default_stake: default_stake ?? defaultStakes.default_stake,
      stake_1: thousandSeparator(stake_1 || defaultStakes.stake_1),
      stake_2: thousandSeparator(stake_2 || defaultStakes.stake_2),
      stake_3: thousandSeparator(stake_3 || defaultStakes.stake_3)
    };
  }, [defaultStakes, storePredefinedStakes]);

  const setPredefinedStakes = useCallback((values: PredefinedStakes) => {
    dispatch(setRSPredefinedStakes(values));
  }, []);

  return { predefinedStakes, setPredefinedStakes };
};
